import styled from "styled-components";
import { CardComp } from "../";

const Pulse = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: invSkeletonPulse 1.4s ease-in-out infinite;
  border-radius: ${(p) => p.$radius || "8px"};
  height: ${(p) => p.$h || "16px"};
  width: ${(p) => p.$w || "100%"};
  max-width: 100%;

  @keyframes invSkeletonPulse {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

/** Shared pulse block for investor portal skeletons. */
export function InvestorSkeletonPulse({ height = "16px", width = "100%", radius = "8px", className = "" }) {
  return <Pulse $h={height} $w={width} $radius={radius} className={className} aria-hidden />;
}

/**
 * Full-page skeleton matching InvestorDashboard layout
 * (hero, summary cards, quick actions, activity + recommendations).
 */
export default function InvestorDashboardSkeleton() {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="visually-hidden">Loading dashboard…</span>

      <div
        className="mb-4 rounded-4 p-4 p-md-5"
        style={{ background: "linear-gradient(135deg, #f3e9ea 0%, #eef4f6 100%)" }}
      >
        <InvestorSkeletonPulse height="14px" width="140px" className="mb-3" />
        <InvestorSkeletonPulse height="32px" width="280px" className="mb-2" />
        <InvestorSkeletonPulse height="16px" width="70%" />
      </div>

      <InvestorSkeletonPulse height="18px" width="200px" className="mb-3" />
      <div className="row g-3 mb-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="col-6 col-lg-3">
            <CardComp cardClassName="border-0 shadow-sm rounded-4 h-100 p-3">
              <InvestorSkeletonPulse height="12px" width="55%" className="mb-3" />
              <InvestorSkeletonPulse height="28px" width="70%" className="mb-2" />
              <InvestorSkeletonPulse height="12px" width="80%" />
            </CardComp>
          </div>
        ))}
      </div>

      <InvestorSkeletonPulse height="18px" width="140px" className="mb-3" />
      <div className="row g-3 mb-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="col-6 col-md-4 col-xl">
            <CardComp cardClassName="border-0 shadow-sm rounded-4 h-100 p-3">
              <InvestorSkeletonPulse height="16px" width="60%" className="mb-2" />
              <InvestorSkeletonPulse height="12px" width="85%" />
            </CardComp>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="d-flex justify-content-between mb-3">
            <InvestorSkeletonPulse height="18px" width="150px" />
            <InvestorSkeletonPulse height="14px" width="100px" />
          </div>
          <CardComp cardClassName="border-0 shadow-sm rounded-4 p-3">
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {[1, 2, 3, 4].map((i) => (
                <InvestorSkeletonPulse key={i} height="32px" width="88px" radius="20px" />
              ))}
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="d-flex gap-3 align-items-start mb-3 pb-3 border-bottom">
                <InvestorSkeletonPulse height="12px" width="12px" radius="50%" />
                <div className="flex-grow-1">
                  <InvestorSkeletonPulse height="14px" width="45%" className="mb-2" />
                  <InvestorSkeletonPulse height="12px" width="70%" />
                </div>
              </div>
            ))}
          </CardComp>
        </div>
        <div className="col-lg-5">
          <InvestorSkeletonPulse height="18px" width="220px" className="mb-3" />
          <div className="d-flex flex-column gap-3">
            {[1, 2].map((i) => (
              <CardComp key={i} cardClassName="border-0 shadow-sm rounded-4 p-3">
                <div className="d-flex justify-content-between mb-2">
                  <InvestorSkeletonPulse height="22px" width="90px" radius="12px" />
                  <InvestorSkeletonPulse height="14px" width="40px" />
                </div>
                <InvestorSkeletonPulse height="18px" width="75%" className="mb-2" />
                <InvestorSkeletonPulse height="12px" width="55%" className="mb-3" />
                <InvestorSkeletonPulse height="14px" width="120px" />
              </CardComp>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
